import { ToastrService } from 'ngx-toastr';
import { Observable } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { t } from '@lib/translation';
import { SnDialogService } from '@lib/ui/dialog';

import { SnLpr, SnLprObject } from '../models';

@Injectable({ providedIn: 'root' })
export class LprHotListAPI {
  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
  ) {}

  public getLprHotlists(params?: ISnListQueryParameters): Observable<ISnListResponse<SnLpr>> {
    return this._httpClient.get('/api/lpr/hotlists/', { params }).pipe(
      map((response: any) => {
        return {
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((list: any) => new SnLpr(list)),
        };
      }),
    );
  }

  public createHotlist(data: any): Observable<SnLpr> {
    return this._httpClient.post('/api/lpr/hotlists/', data).pipe(
      map((response: any) => new SnLpr(response)),
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.created')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.unableToCreate')),
      }),
    );
  }

  public updateHotlist(id: string, data: any): Observable<SnLpr> {
    return this._httpClient.patch(`/api/lpr/hotlists/${id}/`, data).pipe(
      map((response: any) => new SnLpr(response)),
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.updated')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.unableToUpdate')),
      }),
    );
  }

  public deleteHotlist(id: string): Observable<any> {
    return this._dialogService
      .confirm({
        message: t('vehicleIntelligence.hotlist.deleteConfirmation'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/lpr/hotlists/${id}`)),
        tap({
          next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.deleted')),
          error: () =>
            this._toastrService.error(t('vehicleIntelligence.hotlist.couldNotDelete')),
        }),
      );
  }

  public getGroupsLprPlates(
    params?: ISnListQueryParameters,
  ): Observable<ISnListResponse<SnLprObject>> {
    return this._httpClient.get('/api/lpr/hotlist/objects/', { params }).pipe(
      tap({
        error: () =>
          this._toastrService.error(t('vehicleIntelligence.hotlist.failedToObtainData')),
      }),
      map((response: any) => {
        return {
          count: response.count,
          totalPages: response.totalPages,
          results: response.results.map((lprObject: any) => new SnLprObject(lprObject)),
        };
      }),
    );
  }

  public searchLprObject(query: string, id: string): Observable<SnLprObject> {
    return this._httpClient
      .get('/api/lpr/hotlist/objects/', { params: { group: id, search: query } })
      .pipe(
        map((response: any) => {
          return response.results.map((lprObject: any) => new SnLprObject(lprObject));
        }),
      );
  }

  public addPlateToLprObject(plate: any): Observable<SnLprObject> {
    return this._httpClient.post('/api/lpr/hotlist/objects/', plate).pipe(
      map((response: any) => new SnLprObject(response)),
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.plateAdded')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.unableToAddPlate')),
      }),
    );
  }

  public updateLprObject(objId: string, data: any): Observable<SnLprObject> {
    return this._httpClient.patch(`/api/lpr/hotlist/objects/${objId}/`, data).pipe(
      map((response: any) => new SnLprObject(response)),
      tap({
        next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.plateUpdated')),
        error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.unableToUpdatePlate')),
      }),
    );
  }

  public removePlateFromLprObject(plateId: string): Observable<any> {
    return this._dialogService
      .confirm({
        message: t('vehicleIntelligence.hotlist.removePlateConfirmation'),
        confirmButton: {
          text: t('common.delete').toUpperCase(),
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/lpr/hotlist/objects/${plateId}`)),
        tap({
          next: () => this._toastrService.success(t('vehicleIntelligence.hotlist.plateRemoved')),
          error: () => this._toastrService.error(t('vehicleIntelligence.hotlist.couldNotRemovePlate')),
        }),
      );
  }
}
